<template>
    <div class="text-center">
        <!-- Activator Button -->
        <v-btn 
            @click="handleOpen" 
            icon 
            class="activator-button"
            :size="isMobile ? 'default' : 'large'"
        >
            <v-icon icon="mdi-map-marker-multiple" :style="{ color: MAIN_COLOR }"></v-icon>
        </v-btn>
        
        <!-- Dialog -->
        <v-dialog 
            v-model="dialogOpen" 
            :fullscreen="isMobile"
            :max-width="isMobile ? '100%' : '90%'"
            :max-height="isMobile ? '100%' : '90vh'"
            transition="dialog-bottom-transition"
            scrollable
            persistent
        >
            <!-- Dialog Content - v-if ensures complete mount/unmount -->
            <v-card v-if="dialogOpen" class="modal-card" :key="`modal-content-${dialogOpen}`">
                <v-toolbar class="header" :density="isMobile ? 'compact' : 'default'">
                    <img 
                        :width="isMobile ? '24px' : '30px'" 
                        class="ml-2 ml-md-5" 
                        :src="`${baseUrl}images/logo.png`"
                        alt="Logo"
                    >
                    <v-toolbar-title :style="{ color: MAIN_COLOR }" class="title">
                        {{ $t('interests') }}
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn 
                        icon="mdi-close" 
                        @click="handleClose" 
                        color="white"
                        :size="isMobile ? 'small' : 'default'"
                        variant="text"
                    ></v-btn>
                </v-toolbar>
                <v-card-text class="pa-0">
                    <v-virtual-scroll 
                        :items="pointsOfInterest" 
                        :item-height="itemHeight"
                        :key="`virtual-scroll-${language.value || 'gb'}`"
                        class="virtual-scroll-container"
                    >
                        <template v-slot:default="{ item, index }">
                            <div 
                                class="poi-item" 
                                :class="{ 'mobile-item': isMobile, 'desktop-item': !isMobile }"
                            >
                                <div class="carousel-wrapper">
                                    <v-carousel
                                        :height="carouselHeight"
                                        :show-arrows="!isMobile"
                                        :show-arrows-on-hover="!isMobile"
                                        hide-delimiters
                                        cycle
                                        :interval="5000"
                                    >
                                        <v-carousel-item 
                                            v-for="(img, i) in item?.imgs || []"
                                            :src="img"
                                            cover
                                            :key="`${item.id}-${i}`"
                                            :id="`carousel-${item.id}-${i}`"
                                            class="carousel-item"
                                        >
                                            <div class="carousel-overlay">
                                                <v-container class="overlay-content">
                                                    <p class="point-name" :style="{ color: MAIN_COLOR }">
                                                        {{ getPointTranslation(item, 'name') || $t(`points.${item.id}.name`) || item.id }}
                                                    </p>
                                                    <p class="point-description">
                                                        {{ getPointTranslation(item, 'description') || $t(`points.${item.id}.description`) || '' }}
                                                    </p>
                                                </v-container>
                                            </div>
                                        </v-carousel-item>
                                    </v-carousel>
                                </div>
                            </div>
                        </template>
                    </v-virtual-scroll>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { usePointsOfInterest } from '../hook/usePointsOfInterest';
import { useLanguage } from '../hook/useLanguage';
import { MAIN_COLOR, SCROLL_DELAY } from '../constants';

const baseUrl = import.meta.env.BASE_URL || '/';

const props = defineProps({
  open: {
    type: Boolean,
    required: true
  },
  id: {
    type: String,
  },
});

// Try to get display, fallback to window width if not available
let display;
try {
  display = useDisplay();
} catch (e) {
  console.warn('useDisplay not available, using window width fallback');
  display = null;
}

const { data: pointsOfInterest } = usePointsOfInterest();
const { language } = useLanguage();

// Helper function to get translation from content.json or fallback to i18n
// This function is reactive because it accesses language.value which Vue tracks
// Vue will re-execute this function when language.value changes
const getPointTranslation = (item, key) => {
  if (!item || !language.value) return null;
  // Directly access language.value - Vue will track this reactive dependency
  const translation = item.translations?.[language.value]?.[key];
  if (translation) return translation;
  // Fallback to i18n if translation not found in content.json
  return null;
};

// Watch language changes to ensure translations update in the modal
// The key binding on virtual-scroll will also force a re-render when language changes
watch(() => language.value, () => {
  // Language changed - translations will update via reactivity
  // The :key binding ensures the virtual scroll re-renders
}, { immediate: false });

const emit = defineEmits(['fullScreenModal:close', 'fullScreenModal:open']);

// Local dialog state that syncs with prop
const dialogOpen = ref(props.open);

// Watch for prop changes
watch(() => props.open, (newValue) => {
  dialogOpen.value = newValue;
});

// Watch for dialog state changes and emit
watch(dialogOpen, (newValue) => {
  if (!newValue) {
    // Dialog was closed
    emit('fullScreenModal:close');
  }
});

// Helper function to get window width safely
const getWindowWidth = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth;
  }
  return 1024; // Default to desktop
};

// Computed properties for responsive behavior with safe access
const isMobile = computed(() => {
  if (display && display.mobile) {
    try {
      return display.mobile.value;
    } catch {
      return getWindowWidth() < 960;
    }
  }
  return getWindowWidth() < 960;
});

const isTablet = computed(() => {
  if (display && display.tablet) {
    try {
      return display.tablet.value;
    } catch {
      const width = getWindowWidth();
      return width >= 960 && width < 1264;
    }
  }
  const width = getWindowWidth();
  return width >= 960 && width < 1264;
});

const isDesktop = computed(() => {
  if (display && display.mobile && display.tablet) {
    try {
      return !display.mobile.value && !display.tablet.value;
    } catch {
      return getWindowWidth() >= 1264;
    }
  }
  return getWindowWidth() >= 1264;
});

// Responsive carousel height
const carouselHeight = computed(() => {
  if (isMobile.value) {
    return '60vh'; // 60% of viewport height on mobile
  } else if (isTablet.value) {
    return '500px'; // Fixed height on tablet
  } else {
    return '600px'; // Fixed height on desktop
  }
});

// Responsive item height for virtual scroll
const itemHeight = computed(() => {
  if (isMobile.value) {
    return 300; // Smaller items on mobile
  } else {
    return 400; // Larger items on desktop
  }
});

watch(() => props.id, (newId) => {
    if (newId && dialogOpen.value) {
        setTimeout(() => {
            // Try to find the first carousel item for this POI
            const element = document.getElementById(`carousel-${newId}-0`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
            }
        }, SCROLL_DELAY);
    }
}, { immediate: true });

function handleClose() {
  dialogOpen.value = false;
  emit('fullScreenModal:close');
}

function handleOpen() {
  dialogOpen.value = true;
  emit('fullScreenModal:open');
}

</script>

<style scoped>
.header {
    background-color: #101820;
    position: sticky;
    top: 0;
    z-index: 10;
}

.title {
    white-space: normal;
    word-wrap: break-word;
    font-size: 1rem;
}

@media (min-width: 960px) {
    .title {
        font-size: 1.25rem;
    }
}

.modal-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100vh;
}

.virtual-scroll-container {
    height: 100%;
    max-height: calc(100vh - 64px); /* Subtract toolbar height */
}

@media (min-width: 960px) {
    .virtual-scroll-container {
        max-height: calc(90vh - 64px);
    }
}

.poi-item {
    width: 100%;
    margin-bottom: 0;
}

.poi-item.mobile-item {
    margin-bottom: 0;
}

.poi-item.desktop-item {
    margin-bottom: 1rem;
    padding: 0.5rem;
}

.carousel-wrapper {
    position: relative;
    width: 100%;
    border-radius: 0;
    overflow: hidden;
}

@media (min-width: 960px) {
    .carousel-wrapper {
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
}

.carousel-item {
    position: relative;
}

.carousel-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.8) 0%,
        rgba(0, 0, 0, 0.6) 50%,
        rgba(0, 0, 0, 0) 100%
    );
    padding: 1rem;
    z-index: 2;
}

@media (min-width: 960px) {
    .carousel-overlay {
        padding: 1.5rem 2rem;
    }
}

.overlay-content {
    padding: 0 !important;
    margin: 0 !important;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.point-name {
    text-shadow: 
        -1px -1px 0 #101820,
        1px -1px 0 #101820,
        -1px 1px 0 #101820,
        1px 1px 0 #101820,
        0 0 4px rgba(0, 0, 0, 0.8);
    font-size: 1rem;
    font-weight: bold;
    color: #ffffff;
    margin: 0;
    line-height: 1.3;
}

@media (min-width: 960px) {
    .point-name {
        font-size: 1.5rem;
    }
}

.point-description {
    color: #ffffff;
    text-shadow: 
        -1px -1px 0 #101820,
        1px -1px 0 #101820,
        -1px 1px 0 #101820,
        1px 1px 0 #101820,
        0 0 3px rgba(0, 0, 0, 0.8);
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.4;
    opacity: 0.95;
}

@media (min-width: 960px) {
    .point-description {
        font-size: 1rem;
    }
}

.activator-button {
    background-color: #101820;
    transition: transform 0.2s ease;
}

.activator-button:hover {
    transform: scale(1.1);
}

@media (min-width: 960px) {
    .activator-button {
        background-color: #101820;
    }
}

/* Improve touch targets on mobile */
@media (max-width: 959px) {
    .activator-button {
        min-width: 48px;
        min-height: 48px;
    }
}

/* Smooth scrolling */
:deep(.v-virtual-scroll) {
    scroll-behavior: smooth;
}

/* Carousel improvements */
:deep(.v-carousel__controls) {
    background: transparent;
}

:deep(.v-carousel__prev),
:deep(.v-carousel__next) {
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
}

:deep(.v-carousel__prev):hover,
:deep(.v-carousel__next):hover {
    background-color: rgba(0, 0, 0, 0.7);
}

/* Image optimization */
:deep(.v-carousel-item img) {
    object-fit: cover;
    width: 100%;
    height: 100%;
}

/* Ensure proper spacing on mobile */
@media (max-width: 959px) {
    .poi-item {
        min-height: 60vh;
    }
}
</style>
